class MevCollectionSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.track = this.querySelector('[data-mev-track]');
    this.previousButton = this.querySelector('[data-mev-prev]');
    this.nextButton = this.querySelector('[data-mev-next]');
    this.currentLabel = this.querySelector('[data-mev-current]');
    this.totalLabel = this.querySelector('[data-mev-total]');
    this.statusLabel = this.querySelector('[data-mev-status]');
    this.loadingLabel = this.querySelector('[data-mev-loading]');
    this.sectionId = this.dataset.sectionId;
    this.loading = false;

    if (!this.track) return;

    this.dataset.ready = 'true';
    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);
    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.update, { passive: true });
    }

    this.update();
    this.announce();
    if (this.hasAttribute('data-autoplay')) this.setupAutoplay();
  }

  disconnectedCallback() {
    this.autoplayEvents?.abort();
    this.autoplayObserver?.disconnect();
    window.clearInterval(this.autoplayTimer);
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKeydown);
    window.clearTimeout(this.scrollStopTimer);
    window.removeEventListener('resize', this.update);
  }

  get items() {
    return Array.from(this.track.querySelectorAll('[data-mev-item]'));
  }

  setupAutoplay() {
    const button = this.querySelector('[data-mev-playback]');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEvents = new AbortController();
    const options = { signal: this.autoplayEvents.signal };
    let paused = motion.matches;
    let visible = false;
    let hovering = false;
    const paint = () => {
      if (button) {
        button.dataset.paused = String(paused);
        button.setAttribute('aria-label', paused ? button.dataset.play : button.dataset.pause);
      }
      this.dataset.playback = paused ? 'paused' : 'playing';
      this.statusLabel?.setAttribute('aria-live', paused ? 'polite' : 'off');
    };
    const pause = (event) => { if (event.target instanceof Node && button?.contains(event.target)) return; paused = true; paint(); };
    if (button) {
      button.hidden = false;
      button.addEventListener('click', () => { paused = !paused; paint(); }, options);
    }
    this.addEventListener('mouseenter', () => { hovering = true; }, options);
    this.addEventListener('mouseleave', () => { hovering = false; }, options);
    this.addEventListener('focusin', pause, options);
    this.addEventListener('pointerdown', pause, options);
    this.addEventListener('keydown', pause, options);
    motion.addEventListener('change', pause, options);
    this.autoplayObserver = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0.2 });
    this.autoplayObserver.observe(this);
    this.autoplayTimer = window.setInterval(() => {
      if (paused || hovering || !visible || document.hidden || this.loading || this.items.length <= this.visibleItems) return;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (atEnd && !this.nextUrl) {
        this.track.scrollTo({ left: 0, behavior: motion.matches ? 'auto' : 'smooth' });
      } else {
        this.move(1);
      }
    }, 4500);
    paint();
  }

  get nextUrl() {
    return this.track.dataset.nextUrl || '';
  }

  set nextUrl(value) {
    this.track.dataset.nextUrl = value || '';
  }

  get total() {
    const declaredTotal = Number.parseInt(this.track.dataset.total, 10);
    return Number.isFinite(declaredTotal) ? declaredTotal : this.items.length;
  }

  get step() {
    const firstItem = this.items[0];
    if (!firstItem) return this.track.clientWidth;

    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return firstItem.getBoundingClientRect().width + gap;
  }

  get currentIndex() {
    if (!this.step) return 0;
    return Math.max(0, Math.min(this.items.length - 1, Math.round(this.track.scrollLeft / this.step)));
  }

  get visibleItems() {
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const padding = (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0);
    return Math.max(1, Math.floor((this.track.clientWidth - padding + gap + 1) / this.step));
  }

  formatNumber(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  onScroll() {
    this.update();
    window.clearTimeout(this.scrollStopTimer);
    this.scrollStopTimer = window.setTimeout(() => this.announce(), 180);
  }

  announce() {
    if (!this.statusLabel || !this.items.length) return;
    const start = this.currentIndex + 1;
    const visibleItems = this.visibleItems;
    const end = Math.min(this.items.length, start + visibleItems - 1);
    this.statusLabel.textContent = start === end
      ? `Style ${start} of ${this.total}`
      : `Styles ${start} to ${end} of ${this.total}`;
  }

  update() {
    if (!this.track) return;

    window.requestAnimationFrame(() => {
      const index = this.currentIndex;
      const visibleItems = this.visibleItems;
      const visibleEnd = Math.min(this.items.length, index + visibleItems);
      const atStart = this.track.scrollLeft <= 2;
      const atRenderedEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      if (this.currentLabel) {
        const firstVisible = this.formatNumber(index + 1);
        const lastVisible = this.formatNumber(visibleEnd);
        this.currentLabel.textContent = firstVisible === lastVisible ? firstVisible : `${firstVisible}–${lastVisible}`;
      }
      if (this.totalLabel) this.totalLabel.textContent = this.formatNumber(this.total);
      if (this.previousButton) this.previousButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atRenderedEnd && !this.nextUrl;

      if (this.nextUrl && index >= this.items.length - 5) this.loadNextPage();
    });
  }

  async move(direction) {
    const atRenderedEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    if (direction > 0 && atRenderedEnd && this.nextUrl) await this.loadNextPage();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: direction * this.step,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  onKeydown(event) {
    if (event.target !== this.track) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.move(event.key === 'ArrowRight' ? 1 : -1);
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl || !this.sectionId) return;

    this.loading = true;
    this.track.setAttribute('aria-busy', 'true');
    if (this.loadingLabel) this.loadingLabel.hidden = false;

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Could not load collection (${response.status})`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextTrack = documentFragment.querySelector('[data-mev-track]');
      if (!nextTrack) throw new Error("The following page does not contain models");

      nextTrack.querySelectorAll('[data-mev-item]').forEach((item) => this.track.appendChild(item));
      this.nextUrl = nextTrack.dataset.nextUrl;
    } catch (error) {
      console.warn('[Miss Earrings] No se pudieron preparar más modelos.', error);
      this.nextUrl = '';
    } finally {
      this.loading = false;
      this.track.removeAttribute('aria-busy');
      if (this.loadingLabel) this.loadingLabel.hidden = true;
      this.update();
      this.announce();
    }
  }
}

if (!customElements.get('mev-collection-slider')) {
  customElements.define('mev-collection-slider', MevCollectionSlider);
}
